import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { PlusIcon, Trash2Icon, LayoutListIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { SortableQuestionCard } from './SortableQuestionCard';
import type { Section, Question, VisibilityCondition } from './types';

interface SectionContainerProps {
  section: Section;
  questions: Question[];
  allQuestions: Question[];
  isReadOnly: boolean;
  dragHandle?: React.ReactNode;
  onSectionChange: (sectionId: string, field: keyof Section, value: any) => void;
  onToggleCollapse: (sectionId: string) => void;
  onRemoveSection: (sectionId: string) => void;
  onAddQuestion: (sectionId: string) => void;
  onRemoveQuestion: (questionId: string) => void;
  onQuestionChange: (questionId: string, field: keyof Question, value: any) => void;
  onTypeChange: (questionId: string, type: string) => void;
  onOptionChange: (questionId: string, index: number, value: string) => void;
  onAddOption: (questionId: string) => void;
  onRemoveOption: (questionId: string, index: number) => void;
  onToggleCondition: (globalIndex: number) => void;
  onConditionChange: (globalIndex: number, field: keyof VisibilityCondition, value: any) => void;
  onConditionValueToggle: (globalIndex: number, value: string) => void;
  onToggleFlag: (questionId: string) => void;
  onFlagChange: (questionId: string, field: string, value: any) => void;
  onFlagValueToggle: (questionId: string, value: string) => void;
  getGlobalIndex: (question: Question) => number;
}

export const SectionContainer: React.FC<SectionContainerProps> = ({
  section,
  questions: sectionQuestions,
  allQuestions,
  isReadOnly,
  dragHandle,
  onSectionChange,
  onToggleCollapse,
  onRemoveSection,
  onAddQuestion,
  onRemoveQuestion,
  onQuestionChange,
  onTypeChange,
  onOptionChange,
  onAddOption,
  onRemoveOption,
  onToggleCondition,
  onConditionChange,
  onConditionValueToggle,
  onToggleFlag,
  onFlagChange,
  onFlagValueToggle,
  getGlobalIndex,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: `section-drop-${section.id}` });

  const sortableIds = sectionQuestions.map(q => `q-${q.id}`);

  return (
    <div
      className={`group/section rounded-xl border-2 overflow-hidden transition-colors ${
        isOver
          ? 'border-indigo-400 dark:border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/20'
          : 'border-indigo-200 dark:border-indigo-700'
      }`}
    >
      <div className="bg-gradient-to-r from-indigo-50 to-indigo-100/50 dark:from-indigo-900/30 dark:to-indigo-900/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            {dragHandle}
            <button
              onClick={() => onToggleCollapse(section.id)}
              className="p-0.5 hover:bg-indigo-100 dark:hover:bg-indigo-800 rounded transition-colors"
            >
              {section.collapsed
                ? <ChevronRightIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                : <ChevronDownIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              }
            </button>
            <LayoutListIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            {!isReadOnly ? (
              <div className="flex items-center gap-3 flex-1">
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => onSectionChange(section.id, 'title', e.target.value)}
                  className="flex-1 px-2 py-1 text-sm font-medium bg-transparent border-b border-indigo-300 dark:border-indigo-600 focus:outline-none focus:border-indigo-500 text-indigo-900 dark:text-indigo-200 placeholder-indigo-400"
                  placeholder="Section title"
                />
                <input
                  type="text"
                  value={section.description}
                  onChange={(e) => onSectionChange(section.id, 'description', e.target.value)}
                  className="flex-1 px-2 py-1 text-xs bg-transparent border-b border-indigo-300 dark:border-indigo-600 focus:outline-none focus:border-indigo-500 text-indigo-700 dark:text-indigo-300 placeholder-indigo-400"
                  placeholder="Section description (optional)"
                />
              </div>
            ) : (
              <div>
                <span className="text-sm font-medium text-indigo-900 dark:text-indigo-200">
                  {section.title || 'Untitled Section'}
                </span>
                {section.description && (
                  <span className="ml-2 text-xs text-indigo-600 dark:text-indigo-400">
                    {section.description}
                  </span>
                )}
              </div>
            )}
            <span className="bg-indigo-100 dark:bg-indigo-800/50 text-indigo-700 dark:text-indigo-300 rounded-full px-2.5 py-0.5 text-xs font-medium">
              {sectionQuestions.length}
            </span>
          </div>
          {!isReadOnly && (
            <button
              onClick={() => onRemoveSection(section.id)}
              className="p-1 hover:bg-indigo-100 dark:hover:bg-indigo-800 rounded-md transition-all ml-2 opacity-0 group-hover/section:opacity-100"
              title="Remove section (questions will be ungrouped)"
            >
              <Trash2Icon className="h-4 w-4 text-red-500" />
            </button>
          )}
        </div>
      </div>

      {!section.collapsed && (
        <div ref={setNodeRef} className="p-4 space-y-4 bg-indigo-50/20 dark:bg-indigo-900/5 min-h-[60px]">
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {sectionQuestions.map((question) => {
              const globalIndex = getGlobalIndex(question);
              return (
                <SortableQuestionCard
                  key={question.id}
                  question={question}
                  globalIndex={globalIndex}
                  allQuestions={allQuestions}
                  isReadOnly={isReadOnly}
                  onRemove={onRemoveQuestion}
                  onQuestionChange={onQuestionChange}
                  onTypeChange={onTypeChange}
                  onOptionChange={onOptionChange}
                  onAddOption={onAddOption}
                  onRemoveOption={onRemoveOption}
                  onToggleCondition={onToggleCondition}
                  onConditionChange={onConditionChange}
                  onConditionValueToggle={onConditionValueToggle}
                  onToggleFlag={onToggleFlag}
                  onFlagChange={onFlagChange}
                  onFlagValueToggle={onFlagValueToggle}
                />
              );
            })}
          </SortableContext>

          {sectionQuestions.length === 0 && (
            <div className="text-center py-6 border-2 border-dashed border-indigo-200 dark:border-indigo-700 rounded-lg">
              <LayoutListIcon className="h-8 w-8 text-indigo-300 dark:text-indigo-600 mx-auto mb-2" />
              <p className="text-sm text-indigo-400 dark:text-indigo-500">
                Drag questions here or add new ones
              </p>
            </div>
          )}

          {!isReadOnly && (
            <button
              onClick={() => onAddQuestion(section.id)}
              className="w-full py-2 border-2 border-dashed border-indigo-300 dark:border-indigo-600 rounded-lg hover:border-indigo-500 dark:hover:border-indigo-400 transition-colors flex items-center justify-center text-indigo-500 dark:text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300 text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Question to Section
            </button>
          )}
        </div>
      )}
    </div>
  );
};
